// 商行详情 — 销售单针式打印模板编辑/新增
import React, { PureComponent } from 'react';
import qs from 'qs';
import { message, Space, Tooltip } from 'antd-v5';
import { Button, Checkbox, Input, InputNumber, Radio } from '@/components';
import PageHeaderLayout from '../../../../component/page-header-layout/index';
import {
  requestNeedleOrderTemplateAdd,
  requestNeedleOrderTemplateUpdate,
} from '../../../../state/request/tag';
import '../detail.less';
import './stallDotMatrixPrintTemplate.less';
import {
  PRINT_STYLE_OPTIONS,
  PRINT_STYLE_VALUE,
  getPrintStyleValueFromLayout,
  getJolimarkTypeFromRecord,
  toUiPrintStyle,
  API_ALIGN_TO_HEADER_IMAGE,
} from './stallDotMatrixPrintShared';
import {
  HEADER_FIELD_ORDER,
  PRODUCT_FIELD_ORDER,
  AMOUNT_FIELD_ORDER,
  FOOTER_FIELD_ORDER,
  PRINT_FIELD_GROUP,
  getOrderedFields,
  getRequiredCodesInOrder,
  getSaleFieldMeta,
} from './stallDotMatrixPrintFieldMeta';
import {
  buildNeedleOrderTemplateSaveParams,
  normalizeNeedleOrderDetailToRecord,
  splitFieldListToSelectedGroups,
} from './stallDotMatrixSalesTemplatePayload';
import toolTipImg from '../../../../common/images/toolTip.png';
import UploadFile from '@/component/upload';

const TEMPLATE_NAME_MAX = 32;
const CUSTOM_CONTENT_MAX = 200;
/** 241mm 一行两个货：货品相关最多 7 项（含默认必选） */
const PRODUCT_FIELD_MAX_V241_TWO = 7;
/** 120mm 一行一个货：货品相关最多 6 项（含默认必选） */
const PRODUCT_FIELD_MAX_V120_ONE = 6;
/** 字段勾选网格：单格按 6 个汉字宽，超过 6 字占 2 格 */
const FIELD_NAME_GRID_CHAR_COUNT = 6;

const getFieldNameGridSpan = (fieldName) =>
  (fieldName || '').length > FIELD_NAME_GRID_CHAR_COUNT ? 2 : 1;
const DEFAULT_HEADER_IMAGE_HEIGHT = 30;
const DEFAULT_QR_IMAGE_HEIGHT = 100;

const HEADER_IMAGE_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
};

function initialCodesFromOrder(orderArr, fieldGroup) {
  return getRequiredCodesInOrder(orderArr, fieldGroup);
}

function getProductFieldMaxLimit(printStyle) {
  if (printStyle === PRINT_STYLE_VALUE.V120_ONE) return PRODUCT_FIELD_MAX_V120_ONE;
  if (printStyle === PRINT_STYLE_VALUE.V241_TWO) return PRODUCT_FIELD_MAX_V241_TWO;
  return null;
}

function isLimitedProductPrintStyle(printStyle) {
  return getProductFieldMaxLimit(printStyle) != null;
}

function trimProductFieldsToLimit(selectedProduct, printStyle) {
  const max = getProductFieldMaxLimit(printStyle);
  if (max == null || selectedProduct.length <= max) return selectedProduct;
  const required = getRequiredCodesInOrder(PRODUCT_FIELD_ORDER, PRINT_FIELD_GROUP.PRODUCT);
  const optional = selectedProduct.filter((c) => !required.includes(c));
  const keepOpt = optional.slice(0, max - required.length);
  return [...required, ...keepOpt];
}

function initSelectedFields(orderArr, listFromApi, fieldGroup) {
  if (Array.isArray(listFromApi) && listFromApi.length > 0) {
    const set = new Set(listFromApi);
    return orderArr.filter((code) => set.has(code));
  }
  return initialCodesFromOrder(orderArr, fieldGroup);
}

function createFormStateFromRecord(rawRecord) {
  const record = normalizeNeedleOrderDetailToRecord(rawRecord);
  const detail = record.templateDetail || {};
  const fieldGroups = splitFieldListToSelectedGroups(detail);
  const apiPrintStyle = detail.printStyle || getJolimarkTypeFromRecord(record);
  const printStyle = apiPrintStyle
    ? toUiPrintStyle(apiPrintStyle)
    : getPrintStyleValueFromLayout(record.printLayout || '');
  const titleBO = detail.titleBO || {};
  const headerImage = detail.headerImage || {};
  const qrCodeImage = detail.qrCodeImage || {};

  return {
    templateName: ((record && record.templateName) || '').slice(0, TEMPLATE_NAME_MAX),
    printStyle,
    titleVisible: titleBO.showTitle !== false,
    titleRows: titleBO.height != null ? titleBO.height : 48,
    headerImageUrl: headerImage.imageUrl || '',
    headerImageAlign: API_ALIGN_TO_HEADER_IMAGE[headerImage.align] || HEADER_IMAGE_ALIGN.RIGHT,
    headerImageHeight:
      headerImage.height != null ? headerImage.height : DEFAULT_HEADER_IMAGE_HEIGHT,
    selectedHeader: initSelectedFields(
      HEADER_FIELD_ORDER,
      fieldGroups.header,
      PRINT_FIELD_GROUP.HEADER,
    ),
    selectedProduct: trimProductFieldsToLimit(
      initSelectedFields(PRODUCT_FIELD_ORDER, fieldGroups.product, PRINT_FIELD_GROUP.PRODUCT),
      printStyle,
    ),
    selectedAmount: initSelectedFields(
      AMOUNT_FIELD_ORDER,
      fieldGroups.amount,
      PRINT_FIELD_GROUP.AMOUNT,
    ),
    selectedFooter: initSelectedFields(
      FOOTER_FIELD_ORDER,
      fieldGroups.footer,
      PRINT_FIELD_GROUP.FOOTER,
    ),
    customContent: detail.customContent || '',
    qrImageUrl: qrCodeImage.imageUrl || '',
    qrImageAlign: API_ALIGN_TO_HEADER_IMAGE[qrCodeImage.align] || HEADER_IMAGE_ALIGN.RIGHT,
    qrImageHeight: qrCodeImage.height != null ? qrCodeImage.height : DEFAULT_QR_IMAGE_HEIGHT,
  };
}

class StallDotMatrixSalesTemplateEdit extends PureComponent {
  constructor(props) {
    super(props);
    const nav = props.location.state || {};
    const mode = nav.mode === 'edit' ? 'edit' : 'add';
    const record = nav.record || {};
    const hasPrefillRecord = record && record.templateDetail;
    this.stallId = nav.stallId;
    this.templateId = mode === 'edit' ? nav.templateId || (record && record.id) : undefined;
    this.state = {
      mode,
      saving: false,
      ...createFormStateFromRecord(mode === 'edit' || hasPrefillRecord ? record : {}),
    };
  }

  scrollPageToTop = () => {
    window.scrollTo(0, 0);
    if (typeof document !== 'undefined') {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const layoutContent = document.querySelector('.ant-layout-content');
      if (layoutContent) layoutContent.scrollTop = 0;
    }
  };

  componentDidMount() {
    this.scrollPageToTop();
    if (!this.stallId) {
      message.warning('缺少商行信息');
      this.props.history.replace('/booth/stallManager');
      return;
    }
    const nav = this.props.location.state || {};
    if (this.state.mode === 'edit' && (!nav.record || !nav.record.templateDetail)) {
      message.warning('缺少模板数据，请从列表重新进入');
      this.goBackToStallDetail();
    }
  }

  goBackToStallDetail = () => {
    const { history, location } = this.props;
    const { stallId, tab = '18' } = location.state || {};
    if (!stallId) {
      history.push('/booth/stallManager');
      return;
    }
    history.push({
      pathname: '/booth/stallManager/stallManagementDetail',
      state: { stallId },
      search: qs.stringify({ tab: String(tab) }, { addQueryPrefix: true }),
    });
  };

  handleCancel = () => {
    this.goBackToStallDetail();
  };

  handlePrintStyleChange = (e) => {
    const nextStyle = e.target.value;
    const nextMax = getProductFieldMaxLimit(nextStyle);
    this.setState((prev) => {
      if (nextMax == null || prev.selectedProduct.length <= nextMax) {
        return { printStyle: nextStyle };
      }
      const required = getRequiredCodesInOrder(PRODUCT_FIELD_ORDER, PRINT_FIELD_GROUP.PRODUCT);
      const optional = prev.selectedProduct.filter((c) => !required.includes(c));
      const keepOpt = optional.slice(0, nextMax - required.length);
      setTimeout(() => {
        message.warning(`已根据打印样式限制，自动调整货品字段为最多 ${nextMax} 项（含默认）`);
      }, 0);
      return { printStyle: nextStyle, selectedProduct: [...required, ...keepOpt] };
    });
  };

  toggleHeaderField = (code, checked) => {
    const meta = getSaleFieldMeta(code, PRINT_FIELD_GROUP.HEADER);
    if (meta?.required) return;
    this.setState((prev) => ({
      selectedHeader: checked
        ? Array.from(new Set([...prev.selectedHeader, code]))
        : prev.selectedHeader.filter((c) => c !== code),
    }));
  };

  toggleProductField = (code, checked) => {
    const meta = getSaleFieldMeta(code, PRINT_FIELD_GROUP.PRODUCT);
    if (meta?.required) return;
    const { printStyle, selectedProduct } = this.state;
    const maxLimit = getProductFieldMaxLimit(printStyle);
    if (checked) {
      if (maxLimit != null && selectedProduct.length >= maxLimit) {
        message.warning(`最多选择${maxLimit}项（含默认）`);
        return;
      }
      if (!selectedProduct.includes(code)) {
        this.setState({ selectedProduct: [...selectedProduct, code] });
      }
    } else {
      this.setState({ selectedProduct: selectedProduct.filter((c) => c !== code) });
    }
  };

  toggleAmountField = (code, checked) => {
    const meta = getSaleFieldMeta(code, PRINT_FIELD_GROUP.AMOUNT);
    if (meta?.required) return;
    this.setState((prev) => ({
      selectedAmount: checked
        ? Array.from(new Set([...prev.selectedAmount, code]))
        : prev.selectedAmount.filter((c) => c !== code),
    }));
  };

  toggleFooterField = (code, checked) => {
    const meta = getSaleFieldMeta(code, PRINT_FIELD_GROUP.FOOTER);
    if (meta?.required) return;
    this.setState((prev) => ({
      selectedFooter: checked
        ? Array.from(new Set([...prev.selectedFooter, code]))
        : prev.selectedFooter.filter((c) => c !== code),
    }));
  };

  handleSave = async () => {
    const { mode, saving, templateName, customContent } = this.state;
    if (saving) return;

    const name = (templateName || '').trim();
    if (!name) {
      message.error('请输入模板名称');
      return;
    }
    if (name.length > TEMPLATE_NAME_MAX) {
      message.error(`模板名称不超过${TEMPLATE_NAME_MAX}个字符`);
      return;
    }
    if ((customContent || '').length > CUSTOM_CONTENT_MAX) {
      message.error(`自定义说明不超过${CUSTOM_CONTENT_MAX}个字符`);
      return;
    }
    if (!this.stallId) {
      message.error('缺少商行信息');
      return;
    }

    const isEdit = mode === 'edit';
    if (isEdit && !this.templateId) {
      message.error('缺少模板信息');
      return;
    }

    const params = buildNeedleOrderTemplateSaveParams(this.state, {
      stallId: this.stallId,
      ...(isEdit ? { id: this.templateId } : {}),
    });
    const saveRequest = isEdit ? requestNeedleOrderTemplateUpdate : requestNeedleOrderTemplateAdd;

    this.setState({ saving: true });
    try {
      const res = await saveRequest(params);
      if (res?.code === 200) {
        message.success('保存成功');
        this.goBackToStallDetail();
      }
    } catch {
    } finally {
      this.setState({ saving: false });
    }
  };

  renderTooltipIcon = (title) => (
    <Tooltip title={title}>
      <img src={toolTipImg} alt='tooltip' className='salesTemplateTooltipIcon' />
    </Tooltip>
  );

  renderFieldRow = (orderArr, fieldGroup, selectedList, toggleFn) => {
    const fields = getOrderedFields(orderArr, fieldGroup);
    return (
      <div className='salesTemplateFieldGrid'>
        {fields.map((f) => {
          const isWide = getFieldNameGridSpan(f.fieldName) === 2;
          return (
            <div
              key={`${f.fieldGroup}:${f.fieldCode}`}
              className={isWide ? 'salesTemplateFieldCell salesTemplateFieldCell--wide' : 'salesTemplateFieldCell'}
            >
              <Checkbox
                disabled={f.required}
                checked={selectedList.includes(f.fieldCode)}
                onChange={(e) => toggleFn(f.fieldCode, e.target.checked)}
              >
                {f.fieldName}
              </Checkbox>
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const {
      mode,
      templateName,
      printStyle,
      titleVisible,
      titleRows,
      headerImageUrl,
      headerImageAlign,
      selectedHeader,
      selectedProduct,
      selectedAmount,
      selectedFooter,
      customContent,
      qrImageUrl,
      qrImageAlign,
      qrImageHeight,
      headerImageHeight,
      saving,
    } = this.state;

    const hasHeaderImage = !!headerImageUrl;
    const hasQrImage = !!qrImageUrl;

    return (
      <PageHeaderLayout>
        <div className='StallManagementDetail'>
          <div className='stallDotMatrixPrintTemplate stallDotMatrixSalesTemplateEdit'>
            <div className='salesTemplateSection'>
              <div className='sectionHeader'>
                <div className='sectionTitle'>基本设置</div>
              </div>
              <div className='distributionEditForm'>
                <div className='formRow'>
                  <div className='formLabel2'>
                    <span className='formRequiredMark'>*</span>模板名称
                  </div>
                  <div className='formControl'>
                    <Input
                      placeholder='请输入模板名称'
                      maxLength={TEMPLATE_NAME_MAX}
                      showCount
                      value={templateName}
                      onChange={(e) => this.setState({ templateName: e.target.value })}
                      style={{ maxWidth: 400 }}
                    />
                  </div>
                </div>
                <div className='formRow'>
                  <div className='formLabel2'>打印样式</div>
                  <div className='formControl'>
                    <Radio.Group value={printStyle} onChange={this.handlePrintStyleChange}>
                      <Space direction='horizontal'>
                        {PRINT_STYLE_OPTIONS.map((opt) => (
                          <Radio key={opt.value} value={opt.value}>
                            {opt.label}
                          </Radio>
                        ))}
                      </Space>
                    </Radio.Group>
                  </div>
                </div>
              </div>
            </div>

            <div className='salesTemplateSection'>
              <div className='sectionHeader'>
                <div className='sectionTitle'>头部标题</div>
              </div>
              <div className='distributionEditForm'>
                <div className='formRow'>
                  <div className='formLabel2'>标题</div>
                  <div className='formControl'>
                    <Radio.Group
                      value={titleVisible}
                      onChange={(e) => this.setState({ titleVisible: e.target.value })}
                    >
                      <Space>
                        <Radio value={true}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            显示
                            {this.renderTooltipIcon('买家关联的商行抬头或商行开户名作为标题')}
                          </div>
                        </Radio>
                        <Radio value={false}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            不显示
                            {this.renderTooltipIcon('使用打印纸上的标题')}
                          </div>
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </div>
                </div>
                <div className='formRow'>
                  <div className='formLabel2'>标题所占高度</div>
                  <div className='formControl'>
                    {titleVisible ? (
                      '自适应'
                    ) : (
                      <Space>
                        <InputNumber
                          min={0}
                          max={1000}
                          precision={0}
                          value={titleRows}
                          onChange={(v) => this.setState({ titleRows: v })}
                          style={{ width: 100 }}
                        />
                        <span>px</span>
                      </Space>
                    )}
                  </div>
                </div>
                <div className='formRow'>
                  <div className='formLabel2'>
                    头部图片
                    {this.renderTooltipIcon('可上传商行logo，定制图片')}
                  </div>
                  <div className='formControl'>
                    <UploadFile
                      url={headerImageUrl}
                      onChange={(url) => {
                        this.setState({ headerImageUrl: url || '' });
                        if (!url) {
                          this.setState({ headerImageHeight: DEFAULT_HEADER_IMAGE_HEIGHT });
                        }
                      }}
                      folderType={20}
                    />
                    <div className='salesTemplateHint'>
                      支持 jpg、jpeg、png
                      格式；图片宽度不宜超过纸张宽度；上传图片之后才可以设置对齐方式、图片高度。
                    </div>
                    <div className='salesTemplateAlignRow'>
                      <span className='salesTemplateAlignLabel'>对齐方式</span>
                      <Radio.Group
                        value={headerImageAlign}
                        disabled={!hasHeaderImage}
                        onChange={(e) => this.setState({ headerImageAlign: e.target.value })}
                      >
                        <Space>
                          <Radio value={HEADER_IMAGE_ALIGN.LEFT}>左对齐</Radio>
                          <Radio value={HEADER_IMAGE_ALIGN.CENTER}>居中对齐</Radio>
                          <Radio value={HEADER_IMAGE_ALIGN.RIGHT}>右对齐</Radio>
                        </Space>
                      </Radio.Group>
                    </div>
                  </div>
                </div>
                <div className='formRow'>
                  <div className='formLabel2'>图片所占高度</div>
                  <div className='formControl'>
                    <Space>
                      <InputNumber
                        disabled={!hasHeaderImage}
                        min={0}
                        max={1000}
                        precision={0}
                        value={headerImageHeight}
                        onChange={(v) => this.setState({ headerImageHeight: v })}
                        style={{ width: 100 }}
                      />
                      <span>px</span>
                    </Space>
                  </div>
                </div>
              </div>
            </div>

            <div className='salesTemplateSection distributionEditForm'>
              <div className='sectionHeader'>
                <div className='sectionTitle'>头部字段</div>
              </div>
              <div className='formRow'>
                <div className='formLabel2'>基本信息</div>
                <div className='formControl'>
                  {this.renderFieldRow(
                    HEADER_FIELD_ORDER,
                    PRINT_FIELD_GROUP.HEADER,
                    selectedHeader,
                    this.toggleHeaderField,
                  )}
                </div>
              </div>
            </div>

            <div className='salesTemplateSection distributionEditForm'>
              <div className='sectionHeader'>
                <div className='sectionTitle'>表格字段</div>
              </div>
              <div className='formRow'>
                <div className='formLabel2'>货品相关</div>
                <div className='formControl'>
                  {isLimitedProductPrintStyle(printStyle) ? (
                    <div className='salesTemplateHint' style={{ marginBottom: 8 }}>
                      当前打印样式下，货品相关最多选择 {getProductFieldMaxLimit(printStyle)}{' '}
                      项（含默认必选）。
                    </div>
                  ) : null}
                  {this.renderFieldRow(
                    PRODUCT_FIELD_ORDER,
                    PRINT_FIELD_GROUP.PRODUCT,
                    selectedProduct,
                    this.toggleProductField,
                  )}
                </div>
              </div>
              <div className='formRow'>
                <div className='formLabel2'>金额相关</div>
                <div className='formControl'>
                  {this.renderFieldRow(
                    AMOUNT_FIELD_ORDER,
                    PRINT_FIELD_GROUP.AMOUNT,
                    selectedAmount,
                    this.toggleAmountField,
                  )}
                </div>
              </div>
            </div>

            <div
              className='salesTemplateSection distributionEditForm'
              style={{ borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}
            >
              <div className='sectionHeader'>
                <div className='sectionTitle'>尾部字段</div>
              </div>
              <div className='formRow'>
                <div className='formLabel2'>尾部字段</div>
                <div className='formControl'>
                  {this.renderFieldRow(
                    FOOTER_FIELD_ORDER,
                    PRINT_FIELD_GROUP.FOOTER,
                    selectedFooter,
                    this.toggleFooterField,
                  )}
                </div>
              </div>
              <div className='formRow'>
                <div className='formLabel2'>自定义说明</div>
                <div className='formControl'>
                  <Input.TextArea
                    placeholder='可输入额外说明，展示在单据底部'
                    maxLength={CUSTOM_CONTENT_MAX}
                    showCount
                    rows={4}
                    value={customContent}
                    onChange={(e) => this.setState({ customContent: e.target.value })}
                    style={{ maxWidth: 640 }}
                  />
                </div>
              </div>
              <div className='formRow'>
                <div className='formLabel2'>二维码</div>
                <div className='formControl'>
                  <UploadFile
                    url={qrImageUrl}
                    onChange={(url) => {
                      this.setState({ qrImageUrl: url || '' });
                      if (!url) {
                        this.setState({ qrImageHeight: DEFAULT_QR_IMAGE_HEIGHT });
                      }
                    }}
                    folderType={20}
                  />
                  <div className='salesTemplateHint'>
                    支持 jpg、jpeg、png；推荐尺寸
                    200×200；上传图片之后才可以设置对齐方式、图片高度。
                  </div>
                  <div className='salesTemplateAlignRow'>
                    <span className='salesTemplateAlignLabel'>对齐方式</span>
                    <Radio.Group
                      value={qrImageAlign}
                      disabled={!hasQrImage}
                      onChange={(e) => this.setState({ qrImageAlign: e.target.value })}
                    >
                      <Space>
                        <Radio value={HEADER_IMAGE_ALIGN.LEFT}>左对齐</Radio>
                        <Radio value={HEADER_IMAGE_ALIGN.CENTER}>居中对齐</Radio>
                        <Radio value={HEADER_IMAGE_ALIGN.RIGHT}>右对齐</Radio>
                      </Space>
                    </Radio.Group>
                  </div>
                </div>
              </div>
              <div className='formRow'>
                <div className='formLabel2'>图片所占高度</div>
                <div className='formControl'>
                  <Space>
                    <InputNumber
                      disabled={!hasQrImage}
                      min={0}
                      max={1000}
                      precision={0}
                      value={qrImageHeight}
                      onChange={(v) => this.setState({ qrImageHeight: v })}
                      style={{ width: 100 }}
                    />
                    <span>px</span>
                  </Space>
                </div>
              </div>
            </div>

            <div className='distributionEditFooter'>
              <Button onClick={this.handleCancel}>取消</Button>
              <Button type='primary' loading={saving} onClick={this.handleSave}>
                保存
              </Button>
            </div>
          </div>
        </div>
      </PageHeaderLayout>
    );
  }
}

export default StallDotMatrixSalesTemplateEdit;
